import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Crown, Star, Sparkles, ArrowRight, Calendar, CreditCard, ArrowLeft } from 'lucide-react';
import { useGetPublicMembershipPlansQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const MembershipPlans: React.FC = () => {
  const navigate = useNavigate();
  const { data: plansResponse, isLoading } = useGetPublicMembershipPlansQuery();
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);

  usePageMetadata('membership-plans');

  const plans = plansResponse?.data || [];

  const getAccessLevelIcon = (level: string | null) => {
    switch (level?.toLowerCase()) {
      case 'silver':
        return <Star className="w-5 h-5 text-gray-400" />;
      case 'gold':
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'platinum':
        return <Sparkles className="w-5 h-5 text-purple-500" />;
      default:
        return <Star className="w-5 h-5 text-gray-400" />;
    }
  };

  const getAccessLevelColor = (level: string | null) => {
    switch (level?.toLowerCase()) {
      case 'silver':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'gold':
        return 'bg-yellow-50 text-yellow-800 border-yellow-300';
      case 'platinum':
        return 'bg-purple-50 text-purple-800 border-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `$${numAmount.toLocaleString('en-US')}`;
  };

  const handleSelectPlan = (planId: number) => {
    setSelectedPlan(planId);
    navigate(`/membership/purchase/${planId}`);
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Choose Your Membership Plan</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Unlock access to premium apartment bookings with our flexible membership plans. 
            Choose one-time payment or convenient monthly installments.
          </p>
        </div>

        {/* Plans Grid */}
        {plans.length === 0 ? (
          <Card className="p-12 text-center">
            <Crown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Membership Plans Available</h3>
            <p className="text-gray-600">Please check back later for available plans.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <Card
                key={plan.plan_id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  selectedPlan === plan.plan_id ? 'ring-2 ring-gray-800' : ''
                }`}
              >
                {/* Access Level Badge */}
                {plan.access_level && (
                  <div className={`absolute top-4 right-4 ${getAccessLevelColor(plan.access_level)} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 border`}>
                    {getAccessLevelIcon(plan.access_level)}
                    {plan.access_level.charAt(0).toUpperCase() + plan.access_level.slice(1)}
                  </div>
                )}

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  {plan.description && (
                    <p className="text-gray-600 mb-6 text-sm">{plan.description}</p>
                  )}

                  {/* Price */}
                  <div className="mb-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold text-gray-900">
                        {formatCurrency(plan.price)}
                      </span>
                      {plan.payment_type === 'monthly_installment' && plan.monthly_installment_amount && (
                        <span className="text-gray-600 text-sm">
                          /month
                        </span>
                      )}
                    </div>
                    {plan.payment_type === 'monthly_installment' && plan.monthly_installment_count && (
                      <p className="text-sm text-gray-600 mt-1">
                        {plan.monthly_installment_count} monthly payments of {formatCurrency(plan.monthly_installment_amount || 0)}
                      </p>
                    )}
                    {plan.payment_type === 'one_time' && (
                      <p className="text-sm text-gray-600 mt-1">One-time payment</p>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="flex items-center gap-2 text-gray-600 mb-6">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">
                      {plan.duration_days} days access
                    </span>
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        Up to {plan.max_bookings_per_month} bookings per month
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        Access to all available apartments
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">
                        Priority booking support
                      </span>
                    </div>
                    {plan.payment_type === 'monthly_installment' && (
                      <div className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">
                          Flexible monthly payments
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Select Button */}
                  <Button
                    onClick={() => handleSelectPlan(plan.plan_id)}
                    className="w-full bg-gray-800 hover:bg-gray-900 flex items-center justify-center gap-2"
                  >
                    Select Plan
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Membership Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Flexible Payment Options</h3>
                <p className="text-sm text-gray-600">
                  Choose between one-time payment or convenient monthly installments that fit your budget.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Easy Booking Process</h3>
                <p className="text-sm text-gray-600">
                  Book apartments instantly with your active membership. No per-night charges required.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default MembershipPlans;
